import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Container,
  Row,
  Col,
  Table,
  CustomInput,
  Input,
} from "reactstrap";
import style from "./styles.module.scss";
import { FormField } from "../../../utils";
import { FormNumber, FormSelect } from "../Controls";
import LaddaButton from "react-ladda/dist/LaddaButton";
import { SLIDE_LEFT } from "react-ladda/dist/constants";
import { parseDate } from "../../../utils";
import { FormattedDate } from "react-intl";
import { FormattedNumber } from "../../FormattedNumber/FormattedNumber";
import { valueIsEmpty, dateIsEmpty, showToast } from "../../../utils/Helper";
import { Field } from "../../Modal/Order/Field/Field";

const TypeInput = {
  Quantity: "Quantity",
  Total: "Total",
};

const initialState = {
  inputType: TypeInput.Quantity,
};

const ModalCalculator = ({ open, toggle, id, price, services, assetInformation, role }) => {
  const [fields, setFields] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(false);

  useEffect(() => {
    if (open) {
      setFields({
        ...initialState,
        price,
        date:
          new Date().getFullYear() +
          "-" +
          ("0" + (new Date().getMonth() + 1)).slice(-2) +
          "-" +
          ("0" + new Date().getDate()).slice(-2),
      });
    }
  }, [open]);

  const onChange = (name, value) => {
    setFields({ ...fields, [name]: value });
    setErrors({ ...errors, [name]: undefined });
  };

  const handleCalculate = () => {
    const nextErrors = {};
    if (valueIsEmpty(fields.price)) {
      nextErrors.price = "Required field";
    }
    if (valueIsEmpty(fields.inputTypeValue)) {
      nextErrors.inputTypeValue = "Required field";
    }
    if (dateIsEmpty(fields.date)) {
      nextErrors.date = "Required field";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length == 0) {
      setLoading(true);
      setResult(false);

      const body = {
        assetId: id,
        date: fields.date,
        price: fields.price,
        quantity: fields.inputType == TypeInput.Quantity ? fields.inputTypeValue : 0,
        total: fields.inputType == TypeInput.Total ? fields.inputTypeValue : 0,
      };

      if (assetInformation) {
        body.priceisclean = !!assetInformation.AssetPricesDirty ? fields.priceisclean : !fields.priceisclean;
        body.priceisdirty = !body.priceisclean;
        body.ytm = !!fields.ytm;
        body.trt = !!fields.trt;
      }

      services.calculator(body).then(({ result, calculator }) => {
        setLoading(false);
        setResult(calculator);

        if (result.Code === 400) {
          showToast(result.Description, true);
        }
      });
    }
  };

  return (
    <Modal isOpen={open} toggle={toggle} className={style.modal}>
      <ModalHeader toggle={toggle}>Asset calculator</ModalHeader>
      <ModalBody className={style.body}>
        <Container className={style.container}>
          <Row>
            <Col lg="6">
              <Field label={"Date"} error={errors.date}>
                <Input
                  type="date"
                  placeholder={"Date"}
                  readOnly={loading}
                  value={fields.date}
                  onChange={(e) => onChange("date", e.target.value)}
                  invalid={!!errors.date}
                />
              </Field>
            </Col>
            <Col lg="6">
              <FormNumber
                name="price"
                label="Price"
                value={fields.price}
                onChange={onChange}
                error={errors.price}
                decimalScale={assetInformation.SubAssetClassDecimalPlaces}
                dataTypeDecimalName="price"
              />
            </Col>
          </Row>
          <Row>
            <Col lg="6">
              <FormSelect
                name="inputType"
                label="Type"
                selectItems={TypeInputValues}
                getSelectItemId={(i) => i.id}
                getSelectItemName={(i) => i.description}
                value={fields.inputType}
                onChange={onChange}
              />
            </Col>
            <Col lg="6">
              <FormNumber
                name="inputTypeValue"
                label={fields.inputType}
                value={fields.inputTypeValue}
                onChange={onChange}
                error={errors.inputTypeValue}
                dataTypeDecimalName={"quantity"}
              />
            </Col>
          </Row>
          {assetInformation && (
            <React.Fragment>
              <Row>
                <Col lg="12" className={role == "fundManager" && style.checkFunds}>
                  <CustomInput
                    type="checkbox"
                    id="ck-priceclean"
                    name="ck-priceclean"
                    label={`Use ${!!assetInformation.AssetPricesDirty ? "clean" : "dirty"} price`}
                    // className="pl-1 mt-1"
                    checked={fields.priceisclean}
                    onChange={(v) => onChange("priceisclean", !fields.priceisclean)}
                  />
                </Col>
              </Row>
              <Row>
                <Col lg="12" className={role == "fundManager" && style.checkFunds}>
                  <CustomInput
                    type="checkbox"
                    id="ck-ytm"
                    name="ck-ytm"
                    className="mb-2"
                    label="Calculate YTM"
                    checked={fields.ytm}
                    onChange={(v) => onChange("ytm", !fields.ytm)}
                  />
                </Col>
              </Row>
            </React.Fragment>
          )}
          <Row>
            <Col lg="12">
              <LaddaButton
                style={{ zIndex: 0 }}
                className="btn btn-ladda btn-primary mb-2"
                loading={loading}
                disabled={false}
                onClick={() => handleCalculate()}
                data-style={SLIDE_LEFT}
              >
                Calculate
              </LaddaButton>
            </Col>
          </Row>
          {result && (
            <Row>
              <Col lg="12">
                <Result result={result} />
              </Col>
            </Row>
          )}
        </Container>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export const Result = ({ result }) => {
  const items = {
    Principal: result.principal,
    Accruals: result.accrual,
    YTM: result.ytm,
    "Nom. Rate": result.TNA,
    "Current Yield": result.currentyield,
    Total: result.total,
    Duration: result.duration,
    Days: result.days,
  };

  return (
    <div className={style.result}>
      <span className={style.sectionDark}>Totals</span>
      <div className={style.totals}>
        {Object.entries(items).map(([key, value]) => (
          <div className={style.item}>
            <span>{key}</span>
            <span>
              <FormattedNumber value={value} dataTypeDecimalName="default" />
            </span>
          </div>
        ))}
        <div className={style.item}></div>
      </div>

      <span className={style.sectionDark}>Events</span>
      <div className={style.events}>
        <Table striped>
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Quantity</th>
              <th>Cash</th>
            </tr>
          </thead>
          <tbody>
            {result.events.map((event, index) => (
              <tr key={`ev-${index}-${event.date}`}>
                <td>
                  <FormattedDate value={parseDate(event.date)} />
                </td>
                <td>{event.type}</td>
                <td>
                  <FormattedNumber value={event.quantity} dataTypeDecimalName="quantity" />
                </td>
                <td>
                  <FormattedNumber value={event.cash} dataTypeDecimalName="currency" />
                </td>
              </tr>
            ))}
            {result.events.length == 0 && (
              <tr>
                <td colSpan="4" className={style.empty}>
                  No events
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

const TypeInputValues = Object.entries(TypeInput).map(([value, key]) => ({
  id: key,
  description: value,
}));

export default ModalCalculator;
